import shoelaceStyles from '../styles/shoelaceStyles';
import styles from '../styles/styles.scss';
import {css, html, LitElement} from 'lit';
import {customElement, state} from 'lit/decorators.js';
import {createRef, ref, Ref} from 'lit/directives/ref.js';
import {log} from '../controllers/LogController';
import {SearchController} from '../controllers/SearchController';
import {Feature, Point} from 'geojson';
import SlTabGroup from '@shoelace-style/shoelace/dist/components/tab-group/tab-group';
import {CaListMapElement} from './CaListMapElement';
import {CaDetailElement} from './CaDetailElement';
import L from 'leaflet';
import {when} from 'lit/directives/when.js';
import {ConfigurationState} from '../storage/ConfigurationState';
import {AnalyticsController} from '../controllers/AnalyticsController';

// prevent tree shaking
export {};

interface State {
  slug: string | undefined;
  feature: Feature | undefined;
}

@customElement('ca-search')
export class CaSearchElement extends LitElement {
  static override styles = [
    shoelaceStyles,
    styles,
    css`
      :host {
        display: block;
      }

      .tabtitle {
        font-size: 130%;
      }

      sl-button::part(base) {
        transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out;
      }

      sl-button::part(base):hover {
        background-color: var(--ca-color-primary);
        color: var(--sl-color-neutral-0-3rd);
      }

      sl-button::part(base):active {
        background-color: var(--sl-color-neutral-0-2nd);
        color: var(--sl-input-label-color);
      }

      a {
        text-decoration: none;
        color: var(--ca-color-highlight);
        font-weight: 500;
      }
    `,
  ];

  @state()
  selectedFeature: Feature | null = null;

  private _viewTabGroupRef: Ref<SlTabGroup> = createRef();
  private _caListMapRef: Ref<CaListMapElement> = createRef();
  private _caDetailRef: Ref<CaDetailElement> = createRef();

  private _controller!: SearchController;

  override connectedCallback() {
    log(this.constructor.name, 'connectedCallback');
    super.connectedCallback();

    this._controller = SearchController.instance(this);

    window.addEventListener(
      'popstate',
      (ev: PopStateEvent) => {
        log(this.constructor.name, 'popstate', ev.state);
        if (ev.state) {
          this._recallState(ev.state);
        } else {
          this._recallState(this._stringToState(''));
        }
      },
    );
  }

  override render() {
    const searchParams = new URLSearchParams(document.location.search);
    const q = searchParams.get('q');
    if (
      q
      && (this._controller.data['geoJson']?.features)
    ) {
      this._recallState(this._stringToState(q));
    }

    return [
      html`
        <p style="display: flex; flex-wrap: wrap; justify-content: end; gap: .5em">
          <sl-button href="${ConfigurationState.url('register')}">
            <svg slot=prefix style="height: 1.2em" xmlns="http://www.w3.org/2000/svg" width="25" height="23.863"
                 viewBox="0 0 25 23.863">
              <path
                d="M21.318,7.818H27v2.273H21.318ZM19.045,13.5H27v2.273H19.045Zm3.409,5.682H27v2.273H22.455ZM2,24.864a9.091,9.091,0,0,1,18.182,0H17.909a6.818,6.818,0,1,0-13.636,0Zm9.091-10.227a6.818,6.818,0,1,1,6.818-6.818A6.816,6.816,0,0,1,11.091,14.636Zm0-2.273A4.545,4.545,0,1,0,6.545,7.818,4.544,4.544,0,0,0,11.091,12.364Z"
                transform="translate(-2 -1)" fill="currentColor" />
            </svg>
            Eintrag erstellen
          </sl-button>
          <sl-button href="${ConfigurationState.url('login')}">
            <svg slot=prefix style="height: 1.2em" xmlns="http://www.w3.org/2000/svg" width="15.145" height="15"
                 viewBox="0 0 15.145 15">
              <path
                d="M16.5,5.568,15,7.068V3.5H8.25V7.25H4.5V15.5H15V13.432l1.5-1.5v4.324a.744.744,0,0,1-.745.744H3.745A.75.75,0,0,1,3,16.245V6.5L7.5,2h8.246a.753.753,0,0,1,.752.744Zm.584,1.538,1.061,1.061L12.311,14l-1.062,0,0-1.059Z"
                transform="translate(-3 -2)" fill="currentColor" />
            </svg>
            Eintrag bearbeiten
          </sl-button>
        </p>
      `,
      html`
        <ca-form
          @ca-map-feature-requested=${
            (ev: CustomEvent) => {
              this._caListMapRef.value?.showFeature(ev.detail);
            }
          }
        ></ca-form>
      `,
      when(
        this._controller.searchWasManuallyTriggered,
        () => html`
          <sl-tab-group ${ref(this._viewTabGroupRef)}>
            <sl-tab slot="nav" panel="list">
              <span class="tabtitle">Listenansicht</span>
            </sl-tab>
            <sl-tab slot="nav" panel="map">
              <span class="tabtitle">Kartenansicht</span>
            </sl-tab>

            <sl-tab-panel name="list">
              <ca-list
                .selectedFeature=${this.selectedFeature}
                .features=${this._filteredFeatures()}
                @ca-feature-selected=${(ev: CustomEvent) => this._handleFeatureSelected(ev)}
              ></ca-list>
            </sl-tab-panel>
            <sl-tab-panel name="map">
              <ca-list-map
                ${ref(this._caListMapRef)}
                .selectedFeature=${this.selectedFeature}
                .features=${this._controller.data['geoJson']?.features}
                @ca-feature-selected=${(ev: CustomEvent) => this._handleFeatureSelected(ev)}
              ></ca-list-map>
            </sl-tab-panel>
          </sl-tab-group>
        `,
      ),
      html`
        <ca-detail
          ${ref(this._caDetailRef)}
          .feature=${this.selectedFeature}
          @ca-feature-selected=${(ev: CustomEvent) => this._handleFeatureSelected(ev)}
          @ca-map-feature-requested=${
            (ev: CustomEvent) => {
              setTimeout(
                () => {
                  this._viewTabGroupRef.value?.show('map');
                  // workaround
                  this._viewTabGroupRef.value!.style.scrollMarginTop = document.body.style.scrollPaddingTop;
                  this._viewTabGroupRef.value?.scrollIntoView(true);
                  this._caListMapRef.value?.showFeature(ev.detail);
                },
                600,
              );
              this._caDetailRef.value!.closeDrawer();
            }
          }
        ></ca-detail>
      `,
    ];
  }

  protected _stringToState(s: string): State {
    log(this.constructor.name, '_stringToState', s, this._controller.data['geoJson']?.features);
    const feature: Feature | undefined = this._controller.data['geoJson']?.features.find(
      (feature: Feature) => feature.properties?.slug === s,
    );

    return {
      feature: feature,
      slug: s,
    };
  }

  protected _recallState(state: State) {
    log(this.constructor.name, '_recallState', state);
    this._handleFeatureSelected(
      new CustomEvent(
        'ca-feature-selected',
        {
          detail: state?.feature,
          bubbles: true,
          composed: true,
        },
      ),
      true,
    );
  }

  private _handleFeatureSelected(ev: CustomEvent, omitHistory = false) {
    log(this.constructor.name, '_handleFeatureSelected', ev.detail, omitHistory);
    this.selectedFeature = ev.detail;

    if (this.selectedFeature?.properties?.company) {
      document.title = `${this.selectedFeature?.properties?.company} – ${ConfigurationState.defaultPageTitle}`;
      if (!omitHistory) {
        AnalyticsController.logDetail(this.selectedFeature?.properties?.slug);
      }
    } else {
      document.title = ConfigurationState.defaultPageTitle;
    }

    let descriptionEl = <HTMLMetaElement | null>document.querySelector('meta[name="description"]');
    if (null === descriptionEl) {
      descriptionEl = document.createElement('meta');
      descriptionEl.setAttribute('property', 'description');
      document.getElementsByTagName('head')[0].appendChild(descriptionEl);
    }
    if (this.selectedFeature?.properties?.description) {
      descriptionEl.content = this.selectedFeature?.properties?.description;
    } else {
      descriptionEl.content = ConfigurationState.defaultPageDescription;
    }

    const url = new URL(document.location.href);
    const search = new URLSearchParams(url.search);
    if (this.selectedFeature?.properties?.slug) {
      search.set('q', this.selectedFeature?.properties?.slug);
    } else {
      search.delete('q');
    }
    if (this._controller.searchObject.fullText !== '') {
      search.set('fulltext', this._controller.searchObject.fullText);
    } else {
      search.delete('fulltext');
    }

    url.search = search.toString();

    let canonicalEl = <HTMLLinkElement | null>document.querySelector('link[rel="canonical"]');
    if (null === canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.rel = 'canonical';
      document.getElementsByTagName('head')[0].appendChild(canonicalEl);
    }
    canonicalEl.href = url.toString();
    log(
      this.constructor.name,
      '_handleFeatureSelected:properties',
      {
        title: document.querySelector('head > title'),
        description: document.querySelector('meta[name="description"]'),
        canonical: document.querySelector('link[rel="canonical"]'),
      },
    );

    if (!omitHistory) {
      const args: [{}, string, module: URL] = [
        {
          slug: this.selectedFeature?.properties?.slug,
          feature: this.selectedFeature,
        },
        '',
        url,
      ];
      log(this.constructor.name, 'pushState', ...args);
      history.pushState(...args);
    }
  }

  private _filteredFeatures(): Feature[] | undefined {
    {
      const radius = Number(this._controller.searchObject.radiusKm) || 0;
      const lat = Number(this._controller.searchObject.lat);
      const lng = Number(this._controller.searchObject.lng);
      log(this.constructor.name, 'distance', lat, lng, radius);

      return this._controller.data['geoJson']?.features.filter(
        (feature: Feature) => {
          if (!this._controller.searchObject.lat || radius === 0) {
            return true;
          }

          const distance = this._controller.distance(
            L.latLng(lat, lng),
            L.latLng(
              [
                (<Point>feature.geometry).coordinates[1],
                (<Point>feature.geometry).coordinates[0],
              ],
            ),
          );

          // log(this.constructor.name, 'distance', distance);
          return distance <= radius * 1000;
        },
      ).sort(
        (a, b) => {
          return (b.properties?.score ?? 0) - (a.properties?.score ?? 0);
        },
      );
    }
  }
}
